const DEFAULT_LOT = "default_lot";

function normalize(value) {
    return (value == null ? "" : value).toString().trim().toLowerCase();
}

function readLotText(value) {
    if (value == null) return "";
    if (typeof value === "object") return (value.lot != null ? value.lot : value.lotcode || "").toString().trim();
    return value.toString().trim();
}

/** Round to 3 decimals — matches inventory amount inputs in the apps. */
function roundQty(value) {
    const qty = parseFloat(value);
    if (Number.isNaN(qty)) return 0;
    return Math.round((qty + Number.EPSILON) * 1000) / 1000;
}

function parsePositiveAmount(value) {
    const qty = parseFloat(value);
    return qty > 0 && !Number.isNaN(qty) ? qty : 0;
}

/** lotcodes may arrive as an array, a JSON string, or a comma separated string. */
function toLotCodesArray(lotcodes) {
    if (lotcodes == null || lotcodes === "") return [];
    if (Array.isArray(lotcodes)) return lotcodes;
    if (typeof lotcodes === "object") return [lotcodes];
    const text = lotcodes.toString().trim();
    if (text.charAt(0) === "[") {
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) return parsed;
        } catch (error) {
            // fall through to the comma separated read
        }
    }
    return text.split(",");
}

function flattenLotCodes(lotcodes) {
    return toLotCodesArray(lotcodes).reduce((acc, code) => {
        if (Array.isArray(code)) return acc.concat(flattenLotCodes(code));
        acc.push(readLotText(code));
        return acc;
    }, []);
}

/** True when the entry was saved with FIFO instead of a hand picked lot. */
export function isFifoAllocation(allocated) {
    const lot = normalize(allocated?.lot);
    return !lot || lot === DEFAULT_LOT;
}

/**
 * Lot codes an allocation entry actually holds.
 * Invoices and shipments saved with FIFO keep lot as "default_lot" and list the
 * lots they really consumed in lotcodes, so only those lots are allocated.
 */
export function getAllocationLotCodes(allocated) {
    const codes = [];
    const addCode = (value) => {
        const norm = normalize(value);
        if (norm && norm !== DEFAULT_LOT && codes.indexOf(norm) < 0) codes.push(norm);
    };
    if (!isFifoAllocation(allocated)) addCode(readLotText(allocated.lot));
    flattenLotCodes(allocated?.lotcodes).forEach(addCode);
    return codes;
}

/** Lot text to display on the "Inventory Already Allocated" alert. */
export function getAllocationLotLabel(allocated, fallbackLot) {
    if (!isFifoAllocation(allocated)) return readLotText(allocated.lot);
    const codes = flattenLotCodes(allocated?.lotcodes).filter(
        (code) => code && normalize(code) !== DEFAULT_LOT
    );
    if (codes.length) return codes.join(", ");
    const fallback = readLotText(fallbackLot);
    if (fallback && normalize(fallback) !== DEFAULT_LOT) return fallback;
    return normalize(allocated?.lot) === DEFAULT_LOT ? "FIFO" : "";
}

/** Normalize one allocation or a list into a non-empty array. */
export function normalizeAllocatedShipmentList(allocated) {
    if (!allocated) return [];
    const list = Array.isArray(allocated) ? allocated : [allocated];
    return list.filter(Boolean);
}

function formatOneAllocationLine(allocated, index, { numbered } = {}) {
    const lot = getAllocationLotLabel(allocated) || allocated.lot || "";
    const qty = allocated.quantity != null && allocated.quantity !== "" ? allocated.quantity : "";
    const detail = [];
    if (lot) detail.push("lot: " + lot);
    if (qty !== "") detail.push("quantity: " + qty);
    const formType = (allocated.formType || "").toString().trim();
    const refParts = [];
    if (allocated.referenceNumber) refParts.push(allocated.referenceNumber);
    if (allocated.submission_id) refParts.push(allocated.submission_id);
    const where = [formType, refParts.join(" / ")].filter(Boolean).join(" ");
    const body = detail.join(", ") + (where ? " — " + where : "");
    if (numbered) return (index + 1) + ") " + body;
    return body;
}

/**
 * Human-readable "Inventory Already Allocated" body.
 * Supports one allocation (lot pick) or many (quantity exceeds free stock).
 */
export function formatAlreadyAllocatedShipmentAlertMessage(allocated, options = {}) {
    const list = normalizeAllocatedShipmentList(allocated);
    if (!list.length) return "";

    const item = (list[0].item || options.itemName || "item").toString().trim() || "item";
    if (list.length === 1) {
        const line = formatOneAllocationLine(list[0], 0, { numbered: false });
        const itemPart = [item, line].filter(Boolean).join(", ");
        return (
            "The selected item: " +
            itemPart +
            " has already been allocated to another Invoice or Shipment Log and cannot be added to this shipment."
        );
    }

    const lines = list.map((entry, index) => formatOneAllocationLine(entry, index, { numbered: true }));
    return (
        "The selected item " +
        item +
        " cannot be added to this shipment because inventory is already allocated:\n\n" +
        lines.join("\n")
    );
}

/**
 * True when an allocation entry holds the selected lot.
 * A FIFO allocation only reserves the lots listed in its lotcodes, so any other
 * lot of that item stays selectable.
 * Empty lot (`""`) rows still match by batchId, or against other empty/FIFO allocations.
 */
export function allocationBlocksLot(allocated, lot, batchId, itemName) {
    if (!allocated) return false;
    const lotNorm = normalize(lot);
    const allocatedCodes = getAllocationLotCodes(allocated);

    // batchId first so empty-lot inventory sharing a batch still counts as allocated.
    if (batchId && allocated.batchId) {
        if (batchId === allocated.batchId) {
            // A FIFO entry that did not consume this lot must not block it, even when the
            // batch ids line up because both rows point at the same inventory item.
            if (
                lotNorm &&
                lotNorm !== DEFAULT_LOT &&
                isFifoAllocation(allocated) &&
                allocatedCodes.length &&
                allocatedCodes.indexOf(lotNorm) < 0
            ) {
                return false;
            }
            return true;
        }
        // Different batch of the same lot stays free — only the reserved batch is allocated.
        return false;
    }

    const itemNorm = normalize(itemName);
    if (itemNorm && normalize(allocated.item) !== itemNorm) return false;

    // Empty / default lot selection: only collide with empty/FIFO allocations that did not
    // reserve specific named lots. Named lots allocated elsewhere (e.g. "dededed") must not
    // block free lot:"" stock unless batchId matched above.
    if (!lotNorm || lotNorm === DEFAULT_LOT) {
        if (!isFifoAllocation(allocated)) return false;
        if (allocatedCodes.length > 0) return false;
        return true;
    }

    return allocatedCodes.indexOf(lotNorm) >= 0;
}

export function getMatchingAllocations(allocations, options = {}) {
    const { lot, batchId, itemName, currentSubmissionId } = options;
    if (!Array.isArray(allocations) || !allocations.length) return [];
    return allocations.filter((allocated) => {
        if (!allocated) return false;
        if (currentSubmissionId && allocated.submission_id === currentSubmissionId) return false;
        return allocationBlocksLot(allocated, lot, batchId, itemName);
    });
}

export function findBlockingAllocation(allocations, options = {}) {
    const matches = getMatchingAllocations(allocations, options);
    return matches.length ? matches[0] : null;
}

/** Sum of quantities reserved by other shipments/invoices for this lot. */
export function getAllocatedQuantity(allocations, options = {}) {
    return getMatchingAllocations(allocations, options).reduce((sum, allocated) => {
        return sum + parsePositiveAmount(allocated.quantity);
    }, 0);
}

/**
 * On-hand quantity for a lot option / batch row.
 * Prefer totalAmount, then amount, then the sum of visible historial batches.
 */
export function getLotStockAmount(lotOption) {
    if (lotOption == null) return 0;
    if (typeof lotOption === "number") return parsePositiveAmount(lotOption);
    if (typeof lotOption === "string") return parsePositiveAmount(lotOption);

    const total = parsePositiveAmount(lotOption.totalAmount);
    if (total > 0) return total;

    const amount = parsePositiveAmount(lotOption.amount);
    if (amount > 0) return amount;

    if (Array.isArray(lotOption.historial) && lotOption.historial.length) {
        return lotOption.historial.reduce((sum, batch) => {
            if (batch?.hide) return sum;
            return sum + parsePositiveAmount(batch?.amount);
        }, 0);
    }
    return 0;
}

/** Stock still free for this shipment after other allocations. */
export function getRemainingLotAmount(lotOptionOrAvailable, allocations, options = {}) {
    const available = getLotStockAmount(lotOptionOrAvailable);
    const allocatedQty = getAllocatedQuantity(allocations, options);
    return Math.max(0, roundQty(available - allocatedQty));
}

/**
 * Free stock across all lot options for an item.
 * Walks every historial batch so a partial allocation on one batch does not hide
 * free stock on sibling batches or other lots (e.g. tst08/06/2026 + remaining on 333).
 */
export function getTotalRemainingAcrossLots(lotOptions, allocations, options = {}) {
    const { itemName, currentSubmissionId } = options;
    if (!Array.isArray(lotOptions) || !lotOptions.length) return 0;

    let totalRemaining = 0;
    lotOptions.forEach((option) => {
        if (!option || option.isNew) return;
        const lotNorm = normalize(option.lot || option.lotcode);
        const itemNorm = normalize(itemName || option.itemName || option.item);

        if (Array.isArray(option.historial) && option.historial.length) {
            option.historial.forEach((batch) => {
                if (!batch) return;
                const batchId = batch.batchId || batch._id || batch.id || null;
                totalRemaining += getRemainingLotAmount(batch, allocations, {
                    lot: lotNorm || normalize(batch.lot),
                    batchId,
                    itemName: itemNorm,
                    currentSubmissionId,
                });
            });
            return;
        }

        const batchId = option.batchId || option._id || option.id || null;
        totalRemaining += getRemainingLotAmount(option, allocations, {
            lot: lotNorm,
            batchId,
            itemName: itemNorm,
            currentSubmissionId,
        });
    });
    return roundQty(totalRemaining);
}

/**
 * True when another shipment/invoice has reserved the entire on-hand quantity
 * (or stock cannot be determined and any matching allocation exists).
 * Partial allocations must leave the remainder selectable.
 */
export function isLotFullyAllocated(lotOptionOrAvailable, allocations, options = {}) {
    const matches = getMatchingAllocations(allocations, options);
    if (!matches.length) return false;

    const hasStockHint =
        lotOptionOrAvailable != null &&
        (
            typeof lotOptionOrAvailable === "number" ||
            typeof lotOptionOrAvailable === "string" ||
            (typeof lotOptionOrAvailable === "object" && (
                lotOptionOrAvailable.amount != null ||
                lotOptionOrAvailable.totalAmount != null ||
                (Array.isArray(lotOptionOrAvailable.historial) && lotOptionOrAvailable.historial.length > 0)
            ))
        );

    if (!hasStockHint) {
        // String-only lot codes without stock info keep the previous strict block.
        return true;
    }

    return getRemainingLotAmount(lotOptionOrAvailable, allocations, options) <= 0;
}

/**
 * True when the requested amount is greater than remaining free stock.
 * Equality is allowed (e.g. 0.05 requested with 0.05 remaining of 0.1 on hand).
 */
export function exceedsRemainingLotAmount(requestedAmount, lotOptionOrAvailable, allocations, options = {}) {
    const requested = roundQty(requestedAmount);
    if (!(requested > 0)) return false;
    const remaining = getRemainingLotAmount(lotOptionOrAvailable, allocations, options);
    return requested > remaining;
}
